import { useMemo } from 'react';
import { useRouteParams } from '@/context/routeParamsContext';
import { useRouterLocation } from '@americanexpress/one-app-router';
import { LoungeTypes } from '@/constants';
import { Lounge } from '@/types';
import { queryToFilters, filterLounges } from '@/helpers/airportFiltersHelper';
import { useTranslatedAirportLounges } from './loungesHelperHooks';
import { useCommonContent } from '../oneCmsIntlHooks/oneCmsIntlHelperHooks';

interface UseFilteredAirportLoungesReturn {
  lounges: Lounge[];
  filteredLounges: Lounge[];
  appliedFilters: Record<string, string[]>;
  isLoading: boolean;
  error: unknown;
  isFiltersEnabled: boolean;
  isSidecarEnabled: boolean;
}

const useFilteredAirportLounges = (): UseFilteredAirportLoungesReturn => {
  const location = useRouterLocation();
  const { product } = useRouteParams() as { product: string };
  const { data, error, isLoading } = useTranslatedAirportLounges();
  const {
    config: { features = [] },
  } = useCommonContent();

  const isFiltersEnabled = features.some(
    (feat: { name: string; enabled: boolean }) =>
      feat.name === 'loungeFilters' && feat.enabled
  );

  const isSidecarEnabled =
    features.some(
      (feat: { name: string; enabled: boolean }) =>
        feat.name === 'sidecar' && feat.enabled
    ) && !product?.includes('idc');

  const appliedFilters = queryToFilters(location.search) as Record<
    string,
    string[]
  >;

  const lounges = useMemo(() => {
    const experienceRank: Record<string, number> = {
      [LoungeTypes.CENTURION]: 0,
      [LoungeTypes.SIDECAR]: 1,
      [LoungeTypes.PARTNER]: 2,
    };

    return (data || [])
      .filter(Boolean)
      .filter(
        (lounge: Lounge) =>
          lounge.experience_type !== LoungeTypes.SIDECAR || isSidecarEnabled
      )
      .sort((a: Lounge, b: Lounge) => {
        const terminalCompare = (a.location?.terminal || '').localeCompare(
          b.location?.terminal || ''
        );
        if (terminalCompare !== 0) return terminalCompare;

        const firstLoungeRank =
          experienceRank[
            (a as Record<string, unknown>)?.experienceType as string
          ];
        const secondLoungeRank =
          experienceRank[
            (b as Record<string, unknown>)?.experienceType as string
          ];
        if (firstLoungeRank !== undefined && secondLoungeRank !== undefined) {
          return firstLoungeRank - secondLoungeRank;
        }
        return 0;
      });
  }, [data, isSidecarEnabled]);

  const filteredLounges = useMemo(() => {
    if (!isFiltersEnabled) return lounges;
    return filterLounges(lounges, appliedFilters);
  }, [lounges, appliedFilters, isFiltersEnabled]);

  return {
    lounges,
    filteredLounges,
    appliedFilters,
    isLoading,
    error,
    isFiltersEnabled,
    isSidecarEnabled,
  };
};

export default useFilteredAirportLounges;
